import {
  INTERVALOS_MIN,
  type EstadoDoMotor,
  type LinhaMorta,
  type Modo,
} from '@sheafyield/sync';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { Botao } from '../ds/Botao.js';
import { Linha } from '../ds/Linha.js';
import { Selecao } from '../ds/Selecao.js';
import './ajustes.css';

export interface MotorDeAjustes {
  sincronizarAgora(): Promise<EstadoDoMotor>;
  getModo(): Promise<Modo>;
  setModo(m: Modo): Promise<void>;
  getIntervaloMin(): Promise<number>;
  setIntervaloMin(n: number): Promise<void>;
  relatorioDeMortas(): Promise<LinhaMorta[]>;
  reenfileirarMortas(): Promise<{
    reenfileiradas: number;
    cursoresZerados: string[];
  }>;
}

const NOME_DA_FASE: Record<EstadoDoMotor['fase'], string> = {
  desligado: 'sem conta',
  ocioso: 'em dia',
  sincronizando: 'sincronizando…',
  semRede: 'sem rede',
  erro: 'com erro',
};
/** Quem decide que "sem rede" não é erro é o motor (fase `semRede`). A tela
 *  guarda a segunda linha de defesa: entre o aparelho perder a rede e o
 *  próximo ciclo, o estado empurrado ainda é o `erro` anterior, e uma lista
 *  vermelha de "Failed to fetch" não pode aparecer nesse intervalo. Fonte:
 *  os eventos `online`/`offline` do próprio aparelho. */
function assinarRede(aoMudar: () => void): () => void {
  window.addEventListener('online', aoMudar);
  window.addEventListener('offline', aoMudar);
  return () => {
    window.removeEventListener('online', aoMudar);
    window.removeEventListener('offline', aoMudar);
  };
}
function useNaRede(): boolean {
  return useSyncExternalStore(
    assinarRede,
    () => navigator.onLine !== false,
    () => true,
  );
}

const NOME_DO_MODO: Record<Modo, string> = {
  manual: 'Só quando eu mandar',
  aoAbrir: 'Ao abrir o app',
  periodico: 'De tempos em tempos',
  continuo: 'Contínuo',
};

function relativo(iso: string | null): string {
  if (!iso) return 'nunca';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'agora há pouco';
  if (min < 60) return `há ${String(min)} min`;
  return new Date(iso).toLocaleString('pt-BR');
}

export function Sincronizacao({
  motor,
  estado,
}: {
  motor: MotorDeAjustes;
  estado: EstadoDoMotor;
}) {
  const [modo, setModo] = useState<Modo>('continuo');
  const [intervalo, setIntervalo] = useState(30);
  const [mortas, setMortas] = useState<LinhaMorta[] | null>(null);
  const naRede = useNaRede();
  useEffect(() => {
    void motor.getModo().then(setModo);
    void motor.getIntervaloMin().then(setIntervalo);
  }, [motor]);

  return (
    <section className="ajustes__secao" aria-labelledby="sinc">
      <h2 id="sinc">Sincronização</h2>
      {/* Esta linha muda sozinha (o motor empurra o estado): sem região viva,
          apertar "Sincronizar agora" não devolve nada ao VoiceOver. */}
      <p role="status" aria-live="polite">
        Estado:{' '}
        {!naRede && estado.fase === 'erro'
          ? NOME_DA_FASE.semRede
          : NOME_DA_FASE[estado.fase]}{' '}
        · última: {relativo(estado.ultimaSincronizacaoEm)} · {estado.pendentes}{' '}
        pendentes
        {estado.mortas > 0 && ` · ${String(estado.mortas)} não subiram`}
        {estado.realtime === 'ligado' && ' · ao vivo'}
      </p>
      {naRede && estado.erros.length > 0 && (
        <ul role="alert" className="ajustes__erros">
          {estado.erros.map((f) => (
            <Linha
              key={`${f.tabela}-${f.mensagem}`}
              titulo={f.tabela}
              detalhe={f.mensagem}
            />
          ))}
        </ul>
      )}
      <Botao largura="cheia" onClick={() => void motor.sincronizarAgora()}>
        Sincronizar agora
      </Botao>
      <span aria-hidden="true">Modo</span>
      <Selecao
        rotulo="Modo"
        valor={modo}
        opcoes={(Object.keys(NOME_DO_MODO) as Modo[]).map((m) => ({
          valor: m,
          rotulo: NOME_DO_MODO[m],
        }))}
        aoMudar={(valor) => {
          const m = valor as Modo;
          setModo(m);
          void motor.setModo(m);
        }}
      />
      <span aria-hidden="true">Intervalo</span>
      <Selecao
        rotulo="Intervalo"
        valor={String(intervalo)}
        opcoes={INTERVALOS_MIN.map((n) => ({
          valor: String(n),
          rotulo: n < 60 ? `${String(n)} min` : `${String(n / 60)} h`,
        }))}
        aoMudar={(valor) => {
          const n = Number(valor);
          setIntervalo(n);
          void motor.setIntervaloMin(n);
        }}
      />
      {estado.mortas > 0 && (
        <Botao
          variante="secundario"
          largura="cheia"
          onClick={() => void motor.relatorioDeMortas().then(setMortas)}
        >
          Ver o que não subiu
        </Botao>
      )}
      {mortas && (
        <>
          <ul>
            {mortas.map((m) => (
              <li key={m.seq}>
                {m.tabela} {m.id.slice(0, 8)} — {m.motivo}
              </li>
            ))}
          </ul>
          <Botao
            variante="secundario"
            largura="cheia"
            onClick={() =>
              void motor.reenfileirarMortas().then(() => setMortas(null))
            }
          >
            Tentar de novo
          </Botao>
        </>
      )}
    </section>
  );
}
